
































































import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { TranslateResult } from "vue-i18n";
import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { InputSubscriptionUpgradeFindAmount } from "@scrile/api-provider/dist/api/SubscriptionUpgradeProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import { useMaxCurrentSubscription } from "@/hooks/useMaxCurrentSubscription";
import usePurchaseSubscription from "@/hooks/usePurchaseSubscription";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCostByCurrency, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { SubscriptionDetails } from "@/payments/types";
import { EVENTS, SUBSCRIPTION_LEVEL_FRAGMENT } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseSpinner from "@/components/BaseSpinner";
import ModalView from "@/components/ModalView";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import ScreenNamePrinter from "@/components/ScreenNamePrinter";
import SubscriptionConfirmModal from "./components/SubscriptionConfirmModal.vue";
import SubscriptionItem from "./components/SubscriptionItem.vue";

interface SubscriptionLevelInitialAmount {
  id: string;
  amount: number;
}

export default defineComponent({
  name: "UserSubscribeModal",
  components: {
    ScreenNamePrinter,
    PaymentConfirmation,
    BaseAvatar,
    BaseIcon,
    SubscriptionItem,
    ModalView,
    BaseSpinner,
    SubscriptionConfirmModal,
  },
  props: {
    show: { type: Boolean, required: true },
    user: { type: Object as PropType<UserPublicData>, required: true },
    outerProcessing: { type: Boolean, default: false },
    title: { type: String as PropType<TranslateResult | string>, default: $t("Choose subscription") },
    subscriptionLevelId: { type: String },
  },
  setup(props, { emit }) {
    const { authUser } = useAuthUser();

    const processingLevelId = ref("");
    const loading = ref(false);
    const subscriptionLevels = ref<SubscriptionLevel[]>([]);
    const subscriptionLevelsInitialAmount = ref<SubscriptionLevelInitialAmount[]>([]);
    const publicPaymentSystems = ref<PaymentSystemPublic[]>([]);
    const selectedSubscriptionLevel = ref<SubscriptionLevel | null>(null);
    const maxCurrentSubscriptionLevel = ref<SubscriptionLevel | null>(null);
    const { getMaxCurrentSubscription, maxCurrentSubscription } = useMaxCurrentSubscription(props.user.id);

    const mainCurrency = computed(() => getMainCurrencyCode());

    const cost = computed(() => {
      return selectedSubscriptionLevel.value?.pricePackage
        ? getCostByCurrency(selectedSubscriptionLevel.value?.pricePackage, mainCurrency.value)?.cost
        : 0;
    });

    const currentAmount = computed(() => {
      if (!!selectedSubscriptionLevel.value && !!subscriptionLevelsInitialAmount.value) {
        return (
          subscriptionLevelsInitialAmount.value.find(i => i.id === selectedSubscriptionLevel.value?.id)?.amount ??
          cost.value
        );
      }
      return 0;
    });

    const showUpgradeConfirm = computed(
      () =>
        !!selectedSubscriptionLevel.value && !!maxCurrentSubscriptionLevel.value && currentAmount.value !== cost.value
    );
    const showPurchaseConfirm = computed(() => !!selectedSubscriptionLevel.value && !showUpgradeConfirm.value);

    const price = computed(
      () => (cost.value && formatMoneyString(cost.value, { currencyCode: mainCurrency.value })) || undefined
    );

    const subscriptionDetails = computed<SubscriptionDetails>(() => ({
      subscriptionSubject: {
        subjectType: RecurringBillingScheduleSubjectTypeEnum.SUBSCRIPTION,
        subjectId: selectedSubscriptionLevel.value?.id ?? "0",
      },
      pricePackageId: selectedSubscriptionLevel.value?.pricePackageId ?? "0",
      price: price.value,
    }));

    const { performPurchaseSubscription, purchaseInfoDetails } = usePurchaseSubscription(
      selectedSubscriptionLevel,
      subscriptionDetails
    );

    const filteredSubscriptionLevels = computed((): SubscriptionLevel[] => {
      if (!props.subscriptionLevelId) return subscriptionLevels.value;
      const amount =
        subscriptionLevels.value.find(level => level.id === props.subscriptionLevelId)?.pricePackage?.amount ?? 0;
      return subscriptionLevels.value.filter(level => (level?.pricePackage?.amount ?? 0) >= amount);
    });

    const paymentSystemId = computed(() => {
      return publicPaymentSystems.value?.find(publicSystem => publicSystem.currencies.includes(mainCurrency.value))?.id;
    });

    const selectedFeedSubscriptionLevelId = computed(() => {
      if (props.subscriptionLevelId) {
        return props.subscriptionLevelId;
      }
      if (subscriptionLevels.value.length > 0) {
        return subscriptionLevels.value[0].id;
      }
      return "";
    });

    const handleSubscribe = useAuthorisedOnly((newSelectedSubscriptionLevel: SubscriptionLevel) => {
      if (!paymentSystemId.value || processingLevelId.value) return;
      selectedSubscriptionLevel.value = newSelectedSubscriptionLevel;
    });

    const cancelUpgradeLevel = () => {
      selectedSubscriptionLevel.value = null;
      processingLevelId.value = "";
    };

    const subscribe = async () => {
      if (!selectedSubscriptionLevel.value) return;
      try {
        processingLevelId.value = selectedSubscriptionLevel.value.id;
        const response = await performPurchaseSubscription();
        if (!!response) {
          emitter.emit(EVENTS.CONTENT_FEED_PURCHASED, { userId: props.user.id });
          emitter.emit(EVENTS.NOTIFICATION_SHOW, {
            text: $t("Subscribed to {0} for {1}", [
              props.user.screenName,
              currentAmount.value
                ? formatMoneyString(currentAmount.value, { currencyCode: getMainCurrencyCode() })
                : "0",
            ]),
            image: {
              user: props.user,
            },
            button: {
              text: $t("Close"),
              cb: () => {},
            },
            position: "top",
          });
          emit("close", true);
        }
      } finally {
        processingLevelId.value = "";
        selectedSubscriptionLevel.value = null;
      }
    };

    const hasSubscription = computed(() => {
      return filteredSubscriptionLevels.value.some(
        i =>
          maxCurrentSubscriptionLevel.value &&
          maxCurrentSubscriptionLevel.value.pricePackage &&
          i.pricePackage?.amount === maxCurrentSubscriptionLevel.value?.pricePackage.amount
      );
    });

    const isLevelButtonDisabled = (level: SubscriptionLevel) => {
      return (
        !!maxCurrentSubscriptionLevel.value &&
        !!maxCurrentSubscriptionLevel.value.pricePackage &&
        !!level.pricePackage &&
        level.pricePackage.amount <= maxCurrentSubscriptionLevel.value.pricePackage.amount
      );
    };

    const isActiveSubscriptionLevel = (level: SubscriptionLevel) => {
      return (
        !!maxCurrentSubscriptionLevel.value &&
        !!maxCurrentSubscriptionLevel.value.pricePackage &&
        !!level.pricePackage &&
        level.pricePackage.amount === maxCurrentSubscriptionLevel.value.pricePackage.amount
      );
    };

    const initialAmount = async (subscriptionLevel: SubscriptionLevel) => {
      if (!paymentSystemId.value || !authUser.value) return 0;
      const data: InputSubscriptionUpgradeFindAmount = {
        currency: mainCurrency.value,
        paymentSystemId: paymentSystemId.value,
        levelId: subscriptionLevel.id,
      };
      return await providers.SubscriptionUpgradeProvider.initialAmountFind({ data });
    };

    const getSubscriptionLevels = async () => {
      const response = await providers.SubscriptionLevelProvider.find(
        { userIds: [props.user.id] },
        SUBSCRIPTION_LEVEL_FRAGMENT
      );
      if (response.some(level => level.contentCount && level.contentCount > 0)) {
        subscriptionLevels.value = response;
      }
    };

    const getData = async () => {
      try {
        if (props.user.verified) {
          loading.value = true;
          await Promise.all([
            providers.PaymentSystemsProvider.findAllPublic().then(r => (publicPaymentSystems.value = r)),
            getMaxCurrentSubscription(),
            getSubscriptionLevels(),
          ]);
          if (subscriptionLevels.value.length > 0) {
            maxCurrentSubscriptionLevel.value =
              subscriptionLevels.value.find(
                subscription => subscription.id === maxCurrentSubscription.value?.levelId
              ) ?? null;
          }
          emit("levelsLoaded", subscriptionLevels.value);
          for (const subscriptionLevel of filteredSubscriptionLevels.value) {
            if (
              (maxCurrentSubscriptionLevel.value &&
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                subscriptionLevel.pricePackage!.amount > maxCurrentSubscriptionLevel.value.pricePackage!.amount) ||
              !maxCurrentSubscriptionLevel.value
            ) {
              const amount = await initialAmount(subscriptionLevel);
              subscriptionLevelsInitialAmount.value.push({
                id: subscriptionLevel.id,
                amount,
              });
            }
          }
        } else {
          emit("levelsLoaded", []);
        }
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      processingLevelId,
      loading,
      filteredSubscriptionLevels,
      maxCurrentSubscriptionLevel,
      selectedFeedSubscriptionLevelId,
      selectedSubscriptionLevel,
      mainCurrency,
      cost,
      subscriptionLevelsInitialAmount,
      paymentSystemId,
      handleSubscribe,
      purchaseInfoDetails,
      showUpgradeConfirm,
      showPurchaseConfirm,
      currentAmount,
      hasSubscription,
      isActiveSubscriptionLevel,
      isLevelButtonDisabled,
      cancelUpgradeLevel,
      subscribe,
    };
  },
});
